import React, { useMemo, useState } from "react";
import { SearchIcon, XIcon } from "lucide-react";
import { useSeo, faqSchema } from "../../hooks/useSeo";
import { PageHero } from "../../components/ui/PageHero";
import { CtaBand } from "../../components/ui/CtaBand";
import { FaqAccordion } from "../../components/ui/FaqAccordion";
import { FAQS, type Faq } from "../../data/faqs";
const CATEGORIES = ["All", "General", "Pricing", "Process", "Hosting"] as const;
type Cat = (typeof CATEGORIES)[number];
const schema = faqSchema(FAQS);
export function ResourcesFaq() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<Cat>("All");
  useSeo({
    title: "FAQ",
    description: "Answers to common questions about NEXORA pricing, process, hosting and websites.",
    path: "/resources/faq",
    schema
  });
  const results = useMemo<Faq[]>(() => {
    const q = query.trim().toLowerCase();
    return FAQS.filter(f => (cat === "All" || f.category === cat) && (!q || `${f.q} ${f.a}`.toLowerCase().includes(q)));
  }, [query, cat]);
  return <>
      <PageHero crumbs={[{
      label: "Resources",
      href: "/resources"
    }, {
      label: "FAQ"
    }]} eyebrow="FAQ" title="Every question, answered." lead="Search or filter by topic. Can't find what you need? Call or email us." data-id="element-862" data-mp="v80oih41rwo3" />
      <section className="shell section grid gap-10 lg:grid-cols-[minmax(260px,0.6fr)_1.4fr]" data-id="element-863" data-mp="zannr331sjvb">
        <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-864" data-mp="1dzjtgthmtksr">
          <label htmlFor="faq-search" className="sr-only" data-id="element-865" data-mp="191ibyczuttje">Search questions</label>
          <div className="flex items-center gap-3 rounded-full border border-line-strong bg-ink-3 px-4 focus-within:border-accent" data-id="element-866" data-mp="193mbkiqq06dv">
            <SearchIcon className="h-5 w-5 shrink-0 text-muted" aria-hidden data-id="element-867" data-mp="1izz7ln9qmwbs" />
            <input id="faq-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search questions" className="min-h-[52px] min-w-0 flex-1 bg-transparent text-[16px] text-fg placeholder:text-muted focus:outline-none" data-id="element-868" data-mp="1ixv06vcmrf6r" />
            {query && <button type="button" onClick={() => setQuery("")} aria-label="Clear search" className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/5" data-id="element-869" data-mp="1sprocwbf2ii9">
                <XIcon className="h-4 w-4" data-id="element-870" data-mp="22oqlpiqqerpy" />
              </button>}
          </div>
          <div role="group" aria-label="Filter by topic" className="mt-5 flex flex-wrap gap-2" data-id="element-871" data-mp="26o98kk245cvf">
            {CATEGORIES.map(c => <button key={c} type="button" aria-pressed={cat === c} onClick={() => setCat(c)} className={`min-h-[44px] rounded-full border px-4 text-[15px] transition-colors duration-150 ease-out ${cat === c ? "border-fg bg-fg text-ink" : "border-line-strong text-fg-2 hover:text-fg"}`} data-id="element-872" data-mp="2bmkewkt7958w">
                {c}
              </button>)}
          </div>
          <p className="mt-5 text-sm text-muted" aria-live="polite" data-id="element-873" data-mp="irrwsw7x5wc1">{results.length} {results.length === 1 ? "result" : "results"}</p>
        </div>
        <div data-id="element-874" data-mp="es0ze9nw0hyj">
          {results.length > 0 ? <FaqAccordion key={`${cat}-${query}`} items={results} idPrefix="all-faq" data-id="element-875" data-mp="9q1wt1zz1p2n" /> : <div className="rounded-2xl border border-dashed border-line-strong p-10 text-center" data-id="element-876" data-mp="4sjs5nszlw0g">
              <p className="t-h3" data-id="element-877" data-mp="12eh7xdfzfn8p">No matching questions</p>
              <p className="mt-2 text-muted" data-id="element-878" data-mp="xgfpmuqdrrya">Try a different word, or ask us directly — we're happy to help.</p>
              <button type="button" onClick={() => {
            setQuery("");
            setCat("All");
          }} className="mt-6 min-h-[44px] rounded-full border border-line-strong px-5 hover:bg-white/5" data-id="element-879" data-mp="spft3ql6bbwb">
                Reset search
              </button>
            </div>}
        </div>
      </section>
      <CtaBand source="faq" title="Still have a question?" lead="Call or email and you'll hear back from one of the builders." data-id="element-880" data-mp="1l7tfmu127bg3" />
    </>;
}